import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, absUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import JobProgressBar from "@/components/JobProgressBar";
import {
  Music,
  BookOpen,
  GraduationCap,
  ArrowRight,
  Loader2,
  Sparkles,
  CheckCircle2,
  UserCircle2,
  AlertCircle,
} from "lucide-react";

const TYPE_ICONS = {
  music: Music,
  story: BookOpen,
  learning: GraduationCap,
};

const STEP_LABELS = ["Type", "Topic", "Length", "Review"];

// Rough cost estimate per scene (USD). nano-banana image ~$0.04, gpt-5-mini ~$0.002/scene.
const COST_PER_SCENE = 0.044;
const LLM_FIXED = 0.01;

export default function CreateVideo() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [activeChar, setActiveChar] = useState(null);
  const [step, setStep] = useState(1);
  const [typeKey, setTypeKey] = useState(null);
  const [topicId, setTopicId] = useState(null);
  const [length, setLength] = useState("long");
  const [video, setVideo] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/videos/catalog").then((r) => setCatalog(r.data));
    api.get("/characters/active")
      .then((r) => setActiveChar(r.data || null))
      .catch(() => setActiveChar(null));
  }, []);

  const typeEntry = catalog?.types?.find((t) => t.key === typeKey);
  const topicEntry = typeEntry?.topics?.find((t) => t.id === topicId);

  // Estimated scene count: long 5-8 (avg 6), short 3-5 (avg 4)
  const avgScenes = length === "long" ? 6 : 4;
  const costLow = (avgScenes - 1) * COST_PER_SCENE + LLM_FIXED;
  const costHigh = (avgScenes + 2) * COST_PER_SCENE + LLM_FIXED;

  const start = async () => {
    setError(null);
    setLaunching(true);
    try {
      const r = await api.post("/videos", {
        type: typeKey,
        topic: topicId,
        length,
      });
      setVideo(r.data);
      setStep(5);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLaunching(false);
    }
  };

  useEffect(() => {
    if (!video || video.status === "ready" || video.status === "failed") return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/videos/${video.id}`);
        setVideo(r.data);
      } catch {
        /* retry */
      }
    }, 2000);
    return () => clearInterval(id);
  }, [video?.id, video?.status]);

  // Phase1Observability1:progress_eta_v1 — bar value now sourced from
  // backend video.progress_pct via JobProgressBar; legacy local calc dropped.

  if (!catalog) {
    return (
      <div className="flex justify-center py-20 text-stone-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="page-create" className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-stone-800">Create Video</h1>
        <p className="text-sm text-stone-600">
          4 quick steps. Script + scene images are now <strong>REAL</strong>{" "}
          (LLM + nano-banana). Voice and music still MOCKED until you connect
          ElevenLabs / Suno keys in Settings.
        </p>
      </div>

      {/* Stepper */}
      <div data-testid="wizard-stepper" className="flex items-center gap-2">
        {STEP_LABELS.map((label, i) => {
          const idx = i + 1;
          const done = step > idx || (step === 5 && idx === 4);
          const active = step === idx;
          return (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                  done
                    ? "bg-emerald-500 text-white"
                    : active
                    ? "bg-orange-500 text-white"
                    : "bg-stone-200 text-stone-600"
                }`}
              >
                {done ? <CheckCircle2 className="h-4 w-4" /> : idx}
              </div>
              <span
                className={`text-sm ${
                  active ? "font-semibold text-stone-800" : "text-stone-500"
                }`}
              >
                {label}
              </span>
              {idx < STEP_LABELS.length ? (
                <span className="mx-1 text-stone-300">—</span>
              ) : null}
            </div>
          );
        })}
      </div>

      {step === 1 ? (
        <div data-testid="step-type" className="grid gap-4 md:grid-cols-3">
          {catalog.types.map((t) => {
            const Icon = TYPE_ICONS[t.key] || Sparkles;
            const on = typeKey === t.key;
            return (
              <button
                key={t.key}
                data-testid={`type-${t.key}`}
                type="button"
                onClick={() => {
                  setTypeKey(t.key);
                  setTopicId(null);
                }}
                className={`relative rounded-2xl border-2 bg-white/80 p-6 text-left shadow-sm transition ${
                  on
                    ? "border-orange-500 ring-2 ring-orange-300"
                    : "border-orange-100 hover:border-orange-300"
                }`}
              >
                {t.badge ? (
                  <Badge
                    data-testid={`badge-${t.key}`}
                    className={
                      t.priority === 1
                        ? "absolute -top-2 right-4 bg-amber-500 text-white shadow-md hover:bg-amber-500"
                        : "absolute -top-2 right-4 bg-sky-600 text-white hover:bg-sky-600"
                    }
                  >
                    {t.badge}
                  </Badge>
                ) : null}
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-orange-200 to-rose-200 text-orange-700">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="text-lg font-bold text-stone-800">{t.label}</div>
                <p className="mt-1 text-sm text-stone-600">{t.description}</p>
              </button>
            );
          })}
        </div>
      ) : null}

      {step === 2 ? (
        <div data-testid="step-topic" className="space-y-3">
          <p className="text-sm text-stone-600">
            Choose a topic for your <strong>{typeEntry?.label}</strong>.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {typeEntry?.topics?.map((t) => {
              const on = topicId === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`topic-${t.id}`}
                  type="button"
                  onClick={() => setTopicId(t.id)}
                  className={`rounded-lg border bg-white/80 px-4 py-3 text-left text-sm transition ${
                    on
                      ? "border-orange-500 ring-2 ring-orange-300"
                      : "border-orange-100 hover:border-orange-300"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {step === 3 ? (
        <div data-testid="step-length" className="grid gap-4 md:grid-cols-2">
          {[
            {
              key: "long",
              title: "Long-form",
              desc: "3–5 minute episode · 5 scenes + outro",
            },
            {
              key: "short",
              title: "Short",
              desc: "Under 60s · 3 scenes + outro",
            },
          ].map((opt) => {
            const on = length === opt.key;
            return (
              <button
                key={opt.key}
                data-testid={`length-${opt.key}`}
                type="button"
                onClick={() => setLength(opt.key)}
                className={`rounded-2xl border-2 bg-white/80 p-5 text-left shadow-sm transition ${
                  on
                    ? "border-orange-500 ring-2 ring-orange-300"
                    : "border-orange-100 hover:border-orange-300"
                }`}
              >
                <div className="text-lg font-bold text-stone-800">{opt.title}</div>
                <p className="mt-1 text-sm text-stone-600">{opt.desc}</p>
              </button>
            );
          })}
        </div>
      ) : null}

      {step === 4 ? (
        <Card data-testid="step-review" className="border-orange-200 bg-white/80">
          <CardHeader>
            <CardTitle>Review</CardTitle>
            <CardDescription>
              Double-check before we kick off the pipeline.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Type" value={typeEntry?.label} />
            <Row label="Topic" value={topicEntry?.label} />
            <Row label="Length" value={length === "long" ? "Long-form (3–5min)" : "Short (<60s)"} />

            <div
              data-testid="review-character"
              className="flex items-center gap-3 rounded-lg border border-orange-100 bg-amber-50 px-3 py-2"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-orange-200 to-rose-200">
                {activeChar?.id ? (
                  <img
                    src={absUrl(`/api/characters/${activeChar.id}/image/portrait.png`)}
                    alt={activeChar.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <UserCircle2 className="h-5 w-5 text-orange-600" />
                )}
              </div>
              <div className="flex-1">
                <div className="font-medium text-stone-800">
                  {activeChar ? `Starring ${activeChar.name}` : "No active character"}
                </div>
                <div className="text-xs text-stone-600">
                  {activeChar
                    ? `${activeChar.style_label} · referenced by nano-banana for every scene`
                    : "Create and activate a Dom character on the Characters page first."}
                </div>
              </div>
            </div>

            <Row
              label="Estimated cost"
              value={
                <span>
                  ~${costLow.toFixed(2)}–${costHigh.toFixed(2)}{" "}
                  <Badge
                    variant="outline"
                    className="ml-1 border-emerald-300 text-emerald-700"
                  >
                    REAL LLM + nano-banana
                  </Badge>
                </span>
              }
            />

            <div
              data-testid="review-note"
              className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-stone-700"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span>
                Voice, music, and AI video clips will be added once you connect
                ElevenLabs / Suno / Veo keys in <strong>Settings</strong>. For
                now voiceover is silent and BGM is a CC0 jingle — labelled{" "}
                <Badge className="ml-1 bg-amber-500 hover:bg-amber-500">MOCKED</Badge>
                {" "}on the progress list.
              </span>
            </div>

            {!activeChar ? (
              <div
                data-testid="no-character-warning"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                You need an active character before generating. Head to the{" "}
                <strong>Characters</strong> page.
              </div>
            ) : null}

            {error ? (
              <div
                data-testid="create-error"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-red-700"
              >
                {error}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {step === 5 && video ? (
        <Card data-testid="step-progress" className="border-orange-200 bg-white/80">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {video.status === "ready" ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ) : video.status === "failed" ? (
                <Badge className="bg-red-600">failed</Badge>
              ) : (
                <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
              )}
              {video.title}
            </CardTitle>
            <CardDescription>
              {video.type} · {video.length} · {video.status}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Phase1Observability1:progress_eta_v1 — shared bar (auto-hides on terminal) */}
            <JobProgressBar video={video} />
            {/* Fallback bar for terminal (ready) — shows 100% complete */}
            {video.status === "ready" ? (
              <Progress
                value={100}
                className="h-2 bg-orange-100 [&>div]:bg-gradient-to-r [&>div]:from-orange-500 [&>div]:to-rose-500"
              />
            ) : null}
            <div className="space-y-2">
              {video.steps?.map((s) => (
                <div
                  key={s.key}
                  data-testid={`step-${s.key}`}
                  className="flex items-center justify-between rounded-md border border-orange-100 bg-white px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-stone-700">{s.label}</span>
                    <Badge
                      className={
                        s.source === "REAL"
                          ? "bg-emerald-600 hover:bg-emerald-600"
                          : "bg-amber-500 hover:bg-amber-500"
                      }
                    >
                      {s.source}
                    </Badge>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      s.status === "done"
                        ? "border-emerald-300 text-emerald-700"
                        : s.status === "running"
                        ? "border-orange-300 text-orange-700"
                        : s.status === "failed"
                        ? "border-red-300 text-red-700"
                        : "border-stone-200 text-stone-500"
                    }
                  >
                    {s.status}
                  </Badge>
                </div>
              ))}
            </div>
            {video.status === "ready" ? (
              <div className="flex gap-2">
                <Button
                  data-testid="open-in-library"
                  onClick={() => navigate(`/library?highlight=${video.id}`)}
                  className="bg-gradient-to-r from-orange-500 to-rose-500 text-white"
                >
                  Open in Library <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  data-testid="create-another"
                  onClick={() => {
                    setVideo(null);
                    setStep(1);
                    setTypeKey(null);
                    setTopicId(null);
                  }}
                  className="border-orange-300"
                >
                  Create another
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Navigation */}
      {step < 5 ? (
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="border-orange-300"
          >
            Back
          </Button>
          {step < 4 ? (
            <Button
              data-testid="wizard-next"
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && !typeKey) ||
                (step === 2 && !topicId) ||
                (step === 3 && !length)
              }
              className="bg-gradient-to-r from-orange-500 to-rose-500 text-white"
            >
              Next <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              data-testid="wizard-generate"
              onClick={start}
              disabled={launching || !activeChar}
              className="bg-gradient-to-r from-orange-500 to-rose-500 text-white"
            >
              {launching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Generate
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-orange-50 pb-2 last:border-0">
      <span className="text-stone-500">{label}</span>
      <span className="font-medium text-stone-800">{value}</span>
    </div>
  );
}
